<script setup lang="ts">
/**
 * RemoteError - Fallback when an MFE fails to load (or a route doesn't match).
 * When the failed path is known (?from=...), offers a retry that clears the
 * reload guard and re-attempts the navigation.
 * With ?reason=forbidden (router permission guard), shows an access-denied
 * message instead, retrying can't help there.
 */
import { computed, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import Button from "primevue/button";
import { clearAllRemoteReloadGuards } from "@/shared/federation/remote-registry";
import { NAV_TOGGLE_KEY, type NavToggleComponent } from "@/features/layout/nav-toggle";

const route = useRoute();
const router = useRouter();

// A merchant can land here while signed in, so the nav must stay reachable.
const navToggle = inject<NavToggleComponent | null>(NAV_TOGGLE_KEY, null);

const isForbidden = computed(() => route.query.reason === "forbidden");

const fromPath = computed(() => {
  const from = route.query.from;
  return typeof from === "string" && from.startsWith("/") ? from : null;
});

function retry() {
  if (!fromPath.value) {
    return;
  }

  clearAllRemoteReloadGuards();

  void router.replace(fromPath.value);
}
</script>

<template>
  <div v-if="navToggle" :class="$style.toggleRow">
    <component :is="navToggle" />
  </div>
  <div v-if="isForbidden" :class="$style.error">
    <h2>Access denied</h2>
    <p>You don't have permission to view this page. Contact an administrator if you need access.</p>
    <Button label="Go home" @click="router.replace({ name: 'home' })" />
  </div>
  <div v-else :class="$style.error">
    <h2>Something went wrong</h2>
    <p>We had trouble loading this section. This is usually a temporary issue.</p>
    <Button v-if="fromPath" label="Try again" @click="retry" />
  </div>
</template>

<style module>
.toggleRow {
  display: flex;
  align-items: center;
  padding: var(--p-spacing-xs) var(--p-spacing-sm);
}

.error {
  padding: var(--p-spacing-3xl);
  text-align: center;
}

.error h2 {
  color: var(--p-text-color);
  margin-bottom: var(--p-spacing-md);
}

.error p {
  color: var(--p-text-muted-color);
  margin-bottom: var(--p-spacing-lg);
}
</style>
